import React, { useState } from "react";
import {
  Layers,
  Target,
  Flag,
  TrendingUp,
  Gauge,
  Calendar,
  Eye,
  Check,
  CheckCircle,
  Scale,
} from "lucide-react";
import { authenticatedFetch } from "../services/auth";
import {
  formatValue,
  formatPeriod,
  perfColor,
  perfIcon,
  perfPct,
  getPerspectiveIcon,
} from "../utils/formatters";

const DataTable = ({ filteredData, openDetails, updateIndicatorStatus }) => {
  const [markingItems, setMarkingItems] = useState(new Set());

  const API_BASE_URL = process.env.REACT_APP_API_URL || "";

  const handleToggleViewed = async (item, e) => {
    e.stopPropagation(); // Evitar abrir o modal

    if (markingItems.has(item.id)) return;

    try {
      setMarkingItems((prev) => new Set(prev).add(item.id));

      const response = await authenticatedFetch(
        `${API_BASE_URL}/api/indicator-values/${item.id}/mark-viewed`,
        {
          method: "PUT",
        },
      );

      if (response.ok) {
        const currentStatus = item.checked === 'T' || item.checked === true;
        const newStatus = !currentStatus;
        updateIndicatorStatus(item.id, newStatus);
        console.log(
          `Indicador ${newStatus ? "marcado" : "desmarcado"} como visto`,
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(
          `Erro ao alterar status: ${errorData.error || "Erro desconhecido"}`,
        );
      }
    } catch (error) {
      console.error("Erro ao alterar status do indicador:", error);
      alert("Erro ao conectar com o servidor");
    } finally {
      setMarkingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const PerspectiveIcon = ({ perspective }) => {
    const iconName = getPerspectiveIcon(perspective);
    if (iconName === "dollar-sign")
      return <span className="text-green-600">💰</span>;
    if (iconName === "users") return <span className="text-blue-600">👥</span>;
    return <span className="text-gray-600">📊</span>;
  };

  const PerformanceIcon = ({ actual, target, higherIsBetter }) => {
    const iconName = perfIcon(actual, target, higherIsBetter);
    if (iconName === "trending-up")
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (iconName === "trending-down")
      return <TrendingUp className="w-4 h-4 text-red-600 rotate-180" />;
    return <span className="w-4 h-4 text-gray-400">-</span>;
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Perspectiva
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                Indicador
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4" />
                Meta
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Realizado
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4" />
                Performance
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4" />
                Peso
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Período
              </div>
            </th>
            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Visto
              </div>
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Ações
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredData.map((item, index) => (
            <tr
              key={item.id}
              className={`cursor-pointer transition-all duration-200 ${(item.checked === 'T' || item.checked === true)
                ? "bg-green-50 border-l-4 border-green-500 hover:bg-green-100"
                : index % 2 === 0
                  ? "bg-white hover:bg-blue-50"
                  : "bg-gray-50 hover:bg-blue-100"
                }`}

            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <PerspectiveIcon perspective={item.perspective} />
                  <span className="text-sm font-medium text-gray-900">
                    {item.perspective}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="font-medium text-gray-900">
                  {item.indicator}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="font-medium text-gray-700">
                  {formatValue(item.target, item.unit)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-lg font-bold text-gray-900">
                  {formatValue(item.actual, item.unit)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <PerformanceIcon
                    actual={item.actual}
                    target={item.target}
                    higherIsBetter={item.higher_is_better}
                  />
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${perfColor(item.actual, item.target, item.higher_is_better)}`}
                  >
                    {perfPct(item.actual, item.target, item.higher_is_better)}%
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                  title="Peso do indicador no período (usado no cálculo da performance do objetivo)"
                >
                  {item.weight != null ? parseFloat(item.weight) : 1}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-gray-600 font-medium">
                  {formatPeriod(item.period)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <button
                  onClick={(e) => handleToggleViewed(item, e)}
                  disabled={markingItems.has(item.id)}
                  className={`p-2 rounded-lg transition-all duration-200 ${(item.checked === 'T' || item.checked === true)
                    ? "bg-green-100 hover:bg-green-200 text-green-700"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-500"
                    } ${markingItems.has(item.id) ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={
                    (item.checked === 'T' || item.checked === true) ? "Marcar como não visto" : "Marcar como visto"
                  }
                >
                  {markingItems.has(item.id) ? (
                    <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                  ) : (item.checked === 'T' || item.checked === true) ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <div className="w-4 h-4 border-2 border-current rounded-full"></div>
                  )}
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetails(item);
                    console.log("Item clicked:", item);
                    console.log("Checked field:", item.checked);
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${item.checked
                    ? "bg-gray-500 hover:bg-gray-600 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                >
                  <Eye className="w-4 h-4" />
                  Detalhes
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


export default DataTable;
